/**
 * patientAge — the PURE, geriatric-anchor age helper (P1-3). Computes whole-year age from a FHIR
 * date_of_birth so the roster + Member-360 identity can show "Age 78" beside the raw DOB.
 *
 * GUARD (sandbox honesty): the skyCare sandbox carries IMPLAUSIBLE DOBs (e.g. future dates like
 * 2026-05-11). We NEVER render a wrong/negative/absurd age. `ageFromDob` returns null when the DOB is
 * missing, unparseable, in the future, or yields an out-of-range age (< 0 or > 120); callers then show
 * "Age unknown" rather than a misleading number. No PHI is logged here (a DOB is never console.logged).
 */

/** The widest plausible human age in whole years. Anything beyond this is treated as a bad DOB. */
const MAX_PLAUSIBLE_AGE = 120;

/**
 * Whole-year age from an ISO-ish date_of_birth (YYYY-MM-DD or a full ISO timestamp), or null when the
 * DOB is missing/unparseable/future or the derived age is implausible (< 0 or > MAX_PLAUSIBLE_AGE).
 * `now` is injectable for deterministic tests; it defaults to the current date.
 */
export function ageFromDob(dob: string | null | undefined, now: Date = new Date()): number | null {
  if (!dob) return null;
  // Accept a leading YYYY-MM-DD (ignoring any time component). Reject anything that does not start with
  // a calendar date so a malformed token never silently yields NaN-driven nonsense.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]); // 1-12
  const day = Number(m[3]); // 1-31
  // Construct the birth date in UTC and round-trip-validate so impossible calendar dates (e.g. 2026-02-30)
  // are rejected rather than rolled over by the Date constructor.
  const birth = new Date(Date.UTC(year, month - 1, day));
  if (
    birth.getUTCFullYear() !== year ||
    birth.getUTCMonth() !== month - 1 ||
    birth.getUTCDate() !== day
  ) {
    return null;
  }

  let age = now.getUTCFullYear() - year;
  // Subtract a year if this year's birthday has not occurred yet (month/day compare in UTC).
  const beforeBirthday =
    now.getUTCMonth() < month - 1 ||
    (now.getUTCMonth() === month - 1 && now.getUTCDate() < day);
  if (beforeBirthday) age -= 1;

  // GUARD: a future DOB (negative age) or an absurd age is not trustworthy -> "unknown".
  if (age < 0 || age > MAX_PLAUSIBLE_AGE) return null;
  return age;
}

/**
 * Display string for an age beside a DOB: "Age 78" when plausible, else "Age unknown" (never a wrong
 * number). Used by the roster age/identity column and the Member-360 IdentityHeader.
 */
export function ageLabel(dob: string | null | undefined, now: Date = new Date()): string {
  const age = ageFromDob(dob, now);
  return age == null ? "Age unknown" : `Age ${age}`;
}
